<template>
  <span class="sr-only"></span>
</template>

<script setup lang="ts">
import type { LexicalEditor } from "lexical";
import {
  $createRangeSelection,
  $getSelection,
  $isNodeSelection,
  $isRangeSelection,
  $isRootNode,
  $setSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  DRAGOVER_COMMAND,
  DRAGSTART_COMMAND,
  DROP_COMMAND,
} from "lexical";
import { mergeRegister } from "@lexical/utils";
import { onMounted, onUnmounted } from "vue";
import { $createImageNode, $isImageNode, ImageNode } from "../nodes/image/ImageNode";
import { useLexicalComposer } from "lexical-vue";
import {
  EDITOR_INSERT_IMAGE_EVENT,
  INSERT_IMAGE_COMMAND,
  type InsertImagePayload,
} from "./shared";

// Constant values
const TRANSPARENT_IMAGE =
  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
const img = document.createElement("img");
img.src = TRANSPARENT_IMAGE;

// Get the editor instance
const editor = useLexicalComposer();

// Register all required commands with the editor using Vue lifecycle hooks
// let unregister: () => void;

function handleImageEvent(_e: Event) {
  const e = _e as CustomEvent;
  const src = e.detail.src;
  if (!src || typeof src !== "string") {
    return;
  }
  const data: InsertImagePayload = {
    src,
    altText: e.detail.altText || "",
  };
  editor.dispatchCommand(INSERT_IMAGE_COMMAND, data);
}

onMounted(() => {
  // Check if ImageNode is registered
  if (!editor.hasNodes([ImageNode])) {
    throw new Error("ImagesPlugin: ImageNode not registered on editor");
  }

  document.addEventListener(EDITOR_INSERT_IMAGE_EVENT, handleImageEvent);

  const unregister = mergeRegister(
    editor.registerCommand<InsertImagePayload>(
      INSERT_IMAGE_COMMAND,
      payload => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          if ($isRootNode(selection.anchor.getNode())) {
            selection.insertParagraph();
          }
          const imageNode = $createImageNode(payload);
          selection.insertNodes([imageNode]);
        }
        return true;
      },
      COMMAND_PRIORITY_EDITOR
    ),

    // Register drag events handlers
    editor.registerCommand<DragEvent>(
      DRAGSTART_COMMAND,
      event => {
        return onDragStart(event);
      },
      COMMAND_PRIORITY_HIGH
    ),

    editor.registerCommand<DragEvent>(
      DRAGOVER_COMMAND,
      event => {
        return onDragover(event);
      },
      COMMAND_PRIORITY_LOW
    ),

    editor.registerCommand<DragEvent>(
      DROP_COMMAND,
      event => {
        return onDrop(event, editor);
      },
      COMMAND_PRIORITY_HIGH
    )
  );

  onUnmounted(() => {
    document.removeEventListener(EDITOR_INSERT_IMAGE_EVENT, handleImageEvent);
    if (unregister) {
      unregister();
    }
  });
});

// Helper functions
function onDragStart(event: DragEvent): boolean {
  const node = getImageNodeInSelection();
  if (!node) {
    return false;
  }
  const dataTransfer = event.dataTransfer;
  if (!dataTransfer) {
    return false;
  }
  dataTransfer.setData("text/plain", "_");
  dataTransfer.setDragImage(img, 0, 0);
  dataTransfer.setData(
    "application/x-lexical-drag",
    JSON.stringify({
      data: {
        altText: node.__altText,
        height: node.__height,
        key: node.getKey(),
        maxWidth: node.__maxWidth,
        src: node.__src,
        width: node.__width,
      },
      type: "image",
    })
  );

  return true;
}

function onDragover(event: DragEvent): boolean {
  const node = getImageNodeInSelection();
  if (!node) {
    return false;
  }
  if (!canDropImage(event)) {
    event.preventDefault();
  }
  return true;
}

function onDrop(event: DragEvent, editor: LexicalEditor): boolean {
  const node = getImageNodeInSelection();
  if (!node) {
    return false;
  }
  const data = getDragImageData(event);
  if (!data) {
    return false;
  }
  event.preventDefault();
  if (canDropImage(event)) {
    const range = getDragSelection(event);
    node.remove();
    const rangeSelection = $createRangeSelection();
    if (range !== null && range !== undefined) {
      rangeSelection.applyDOMRange(range);
    }
    $setSelection(rangeSelection);
    editor.dispatchCommand(INSERT_IMAGE_COMMAND, data);
  }
  return true;
}

function getImageNodeInSelection(): ImageNode | null {
  const selection = $getSelection();
  if (!$isNodeSelection(selection)) {
    return null;
  }
  const nodes = selection.getNodes();
  const node = nodes[0];
  return $isImageNode(node) ? node : null;
}

function getDragImageData(event: DragEvent): null | InsertImagePayload {
  const dragData = event.dataTransfer?.getData("application/x-lexical-drag");
  if (!dragData) {
    return null;
  }
  const { type, data } = JSON.parse(dragData);
  if (type !== "image") {
    return null;
  }

  return data;
}

function canDropImage(event: DragEvent): boolean {
  const target = event.target;
  return !!(
    target &&
    target instanceof HTMLElement &&
    !target.closest("code, span.editor-image") &&
    target.parentElement &&
    target.parentElement.closest("div.ContentEditable__root")
  );
}

// For TypeScript compatibility with the rangeParent/rangeOffset properties
interface ExtendedDragEvent extends DragEvent {
  rangeOffset?: number;
  rangeParent?: Node;
}

function getDragSelection(event: DragEvent): Range | null | undefined {
  let range;
  const domSelection = window.getSelection();
  if (document.caretRangeFromPoint) {
    range = document.caretRangeFromPoint(event.clientX, event.clientY);
  } else if ((event as ExtendedDragEvent).rangeParent && domSelection !== null) {
    domSelection.collapse(
      (event as ExtendedDragEvent).rangeParent as Node,
      (event as ExtendedDragEvent).rangeOffset || 0
    );
    range = domSelection.getRangeAt(0);
  } else {
    throw Error(`Cannot get the selection when dragging`);
  }
  return range;
}
</script>
